import { Link } from "react-router-dom";
import PhIcon from "./PhIcon";
import { DAYS, daysText, initials } from "@/lib/marketRows";

/* One listing on the marketplace homepage, and the same listing on a person's
   or company's "Your card" page. Both render this, so what someone edits is
   exactly what the homepage shows (pages/MarketHome, pages/YourCardPage).
   Shapes, helpers and styles are in lib/marketRows.js. */

/* `own`: the viewer's card, so the button edits it. `preview`: shown on the
   "Your card" page, where the button is what everybody else sees and does
   nothing. */
export default function MarketRow({ r, own = false, preview = false, onOpen }) {
  const action = preview ? (
    <span className="mr-btn" aria-hidden="true">{r.cta}</span>
  ) : own ? (
    <Link to={r.editHref} className="mr-btn mr-btn-own" onClick={onOpen}>Edit your card</Link>
  ) : (
    <Link to={r.href} className="mr-btn" onClick={onOpen}>{r.cta}</Link>
  );
  return (
    // data-posting-id: lib/impressions counts a Sift post once it is on screen.
    <li className="mr-row" data-posting-id={preview ? undefined : r.postingId}>
      {r.image ? (
        <img src={r.image} alt="" className="mr-av" style={{ borderRadius: r.round ? "50%" : 12 }} />
      ) : (
        <span className="mr-av mr-av-ini" style={{ borderRadius: r.round ? "50%" : 12 }} aria-hidden="true">{initials(r.name || r.title)}</span>
      )}
      <div className="mr-body">
        <p className={`mr-tag mr-tag-${r.kind}`}>{r.tag}</p>
        {preview ? (
          <span className="mr-title">{r.title || "Your title"}</span>
        ) : (
          <Link to={r.href} className="mr-title" onClick={onOpen}>{r.title}</Link>
        )}
        {(r.name || r.place) && (
          <p className="mr-meta">{[r.name, r.place].filter(Boolean).join(", ")}</p>
        )}
        {r.detail && <p className="mr-detail">{r.detail}</p>}
        {(r.days.length > 0 || r.fact) && (
          <div className="mr-facts">
            {r.days.length > 0 && (
              <span className="mr-days">
                <span className="mr-dots" aria-hidden="true">
                  {DAYS.map((d) => (
                    <span key={d} className={r.days.includes(d) ? (r.kind === "job" ? "need" : "on") : ""} />
                  ))}
                </span>
                {daysText(r.days)}
              </span>
            )}
            {r.fact && (
              <span className="mr-fact">{r.factIsCredential && <PhIcon name="check" size={16} className="mr-check" />}{r.fact}</span>
            )}
          </div>
        )}
      </div>
      <div className="mr-side">
        {r.key && <span className="mr-key">{r.key}</span>}
        {r.keyNote && <span className="mr-keynote">{r.keyNote}</span>}
        {action}
      </div>
    </li>
  );
}

/* A row's shape before its data arrives: same sizes as a real row, so the page
   keeps its layout while listings load and nothing jumps when they land. Static
   on purpose; a shimmer reads as busy, and the wait is a second or two. */
export function MarketRowSkeleton({ wide = false }) {
  return (
    <li className="mr-row mr-sk" aria-hidden="true">
      <span className="mr-av mr-sk-b" style={{ borderRadius: "50%" }} />
      <div className="mr-body">
        <span className="mr-sk-b" style={{ width: 96, height: 12 }} />
        <span className="mr-sk-b" style={{ width: wide ? "72%" : "54%", height: 20, marginTop: 4 }} />
        <span className="mr-sk-b" style={{ width: 180, height: 14, marginTop: 4 }} />
        <span className="mr-sk-b" style={{ width: wide ? "88%" : "76%", height: 14, marginTop: 4 }} />
      </div>
      <div className="mr-side">
        <span className="mr-sk-b" style={{ width: 56, height: 22 }} />
        <span className="mr-sk-b" style={{ width: 92, height: 36, marginTop: 12, borderRadius: 8 }} />
      </div>
    </li>
  );
}
